<template>
  <section class="hero">
    <!-- Three.js parametric surface -->
    <div ref="heroCanvas" class="hero-canvas"></div>

    <!-- Gradient overlays -->
    <div class="hero-gradient-top"></div>
    <div class="hero-gradient-bottom"></div>

    <!-- Content -->
    <div class="hero-content">
      <!-- Eyebrow -->
      <div class="hero-eyebrow reveal" ref="eyebrow">
        <span class="eyebrow-dot"></span>
        <span>CVPR 2025 · Nashville</span>
        <span class="eyebrow-sep">·</span>
        <span>40M+ 3D Object Captions</span>
      </div>

      <!-- Giant Title -->
      <h1 class="hero-title reveal" ref="title">
        <span class="title-line title-line-1">MARVEL</span>
        <span class="title-line title-line-2">40M<span class="title-plus">+</span></span>
      </h1>

      <!-- Subtitle -->
      <p class="hero-subtitle reveal" ref="subtitle">
        Multi-Level &amp; Domain-Specific 3D Captioning Dataset<br>
        for High-Fidelity Text-to-3D Content Creation
      </p>

      <!-- Stats row -->
      <div class="hero-stats reveal" ref="stats">
        <div class="stat-chip" v-for="s in statItems" :key="s.label">
          <span class="stat-val">{{ s.value }}</span>
          <span class="stat-label">{{ s.label }}</span>
        </div>
      </div>

      <!-- CTA row -->
      <div class="hero-ctas reveal" ref="ctas">
        <a href="#explorer" class="cta-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          Explore Dataset
        </a>
        <a href="https://arxiv.org/abs/2411.17945" target="_blank" class="cta-secondary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/></svg>
          Read Paper
        </a>
        <a href="https://huggingface.co/datasets/sankalpsinha77/MARVEL-40M" target="_blank" class="cta-secondary">
          🤗 Dataset
        </a>
      </div>

      <!-- Scroll indicator -->
      <div class="scroll-hint">
        <div class="scroll-line"></div>
        <span>scroll</span>
      </div>
    </div>

    <!-- Teaser image -->
    <!-- <div class="hero-teaser reveal" ref="teaser">
      <div class="teaser-frame">
        <img
          src="https://objaverse.allenai.org/objaverse-xl/objaverse-xl.jpg"
          alt="MARVEL 40M+ Dataset Preview"
        />
        <div class="teaser-overlay"></div>
        <div class="teaser-label">Dataset Overview</div>
      </div>
    </div> -->
  </section>
</template>

<script>
export default {
  name: 'HeroSection',

  data() {
    return {
      statItems: [
        { value: '40M+', label: 'Captions' },
        { value: '8.9M', label: '3D Objects' },
        { value: '5',    label: 'Detail Levels' },
        { value: '7',    label: 'Source Datasets' },
      ],
    };
  },

  mounted() {
    this.initCanvas();
    this.$nextTick(() => {
      this.initTitleCanvas();
      const els = [
        this.$refs.eyebrow, this.$refs.title, this.$refs.subtitle,
        this.$refs.stats, this.$refs.ctas,
      ];
      els.forEach((el, i) => {
        if (!el) return;
        setTimeout(() => { el && el.classList.add('visible'); }, 200 + i * 160);
      });
    });
  },

  beforeDestroy() {
    cancelAnimationFrame(this._raf);
    cancelAnimationFrame(this._titleRaf);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._titleResize) window.removeEventListener('resize', this._titleResize);
    const container = this.$refs.heroCanvas;
    if (container) container.innerHTML = '';
  },

  methods: {

    // ── Background: Three.js parametric surface ──────────────
    initCanvas() {
      const container = this.$refs.heroCanvas;
      if (!container) return;
      if (window.THREE) { this._initThree(container); return; }
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
      s.onload = () => this._initThree(container);
      document.head.appendChild(s);
    },

    _initThree(container) {
      /* global THREE */
      const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
      const W = container.offsetWidth;
      const H = container.offsetHeight;

      const scene    = new THREE.Scene();
      const camera   = new THREE.PerspectiveCamera(50, W / H, 0.1, 100);
      camera.position.set(0, 6, 15);
      camera.lookAt(0, -1, 0);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(W, H);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
      container.appendChild(renderer.domElement);

      // Grid geometry
      const SEGS = 60;
      const N    = SEGS + 1;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(N * N * 3);
      const indices   = [];
      for (let iy = 0; iy <= SEGS; iy++) {
        for (let ix = 0; ix <= SEGS; ix++) {
          const idx = iy * N + ix;
          positions[idx * 3]     = (ix / SEGS - 0.5) * 54;
          positions[idx * 3 + 1] = 0;
          positions[idx * 3 + 2] = (iy / SEGS - 0.5) * 54;
        }
      }
      for (let iy = 0; iy < SEGS; iy++) {
        for (let ix = 0; ix < SEGS; ix++) {
          const a = iy * N + ix;
          const b = a + 1, c = a + N, d = c + 1;
          indices.push(a, b, c, b, d, c);
        }
      }
      geometry.setIndex(indices);
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      // Solid surface
      const solidMat = new THREE.MeshBasicMaterial({
        color: isDark ? 0x0d1a4a : 0xdde4ff,
        transparent: true, opacity: isDark ? 0.55 : 0.6, side: THREE.DoubleSide,
      });
      const solidMesh = new THREE.Mesh(geometry, solidMat);
      solidMesh.position.y = -1.8;
      scene.add(solidMesh);

      // Wireframe
      const wireGeo = new THREE.WireframeGeometry(geometry);
      const wireMat = new THREE.LineBasicMaterial({
        color: isDark ? 0x5b7fff : 0x3d5af1, transparent: true, opacity: isDark ? 0.35 : 0.55,
      });
      const wireMesh = new THREE.LineSegments(wireGeo, wireMat);
      wireMesh.position.y = -1.8;
      scene.add(wireMesh);

      // Accent wireframe
      const wireGeo2 = new THREE.WireframeGeometry(geometry);
      const wireMat2 = new THREE.LineBasicMaterial({
        color: isDark ? 0xc77dff : 0x9b3de8, transparent: true, opacity: isDark ? 0.12 : 0.25,
      });
      const wireMesh2 = new THREE.LineSegments(wireGeo2, wireMat2);
      wireMesh2.position.y = -1.8;
      scene.add(wireMesh2);

      // Stars
      const starCount = 400;
      const starPos   = new Float32Array(starCount * 3);
      for (let i = 0; i < starCount; i++) {
        starPos[i * 3]     = (Math.random() - 0.5) * 30;
        starPos[i * 3 + 1] = Math.random() * 4 + 10;
        starPos[i * 3 + 2] = (Math.random() - 0.5) * 20;
      }
      const starGeo = new THREE.BufferGeometry();
      starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
      const starMat = new THREE.PointsMaterial({
        color: isDark ? 0xffffff : 0x3d5af1,
        size: isDark ? 0.04 : 0.03, transparent: true, opacity: isDark ? 0.8 : 0.4,
      });
      // Make stars circular using a canvas texture
// Different colored star textures
const starColors = [
  'rgba(255,255,255,1)',   // white
  'rgba(150,180,255,1)',   // blue
  'rgba(199,125,255,1)',   // purple
  'rgba(0,229,255,1)',     // cyan
  'rgba(255,200,100,1)',   // warm yellow
];

// Create one texture per color and make separate Points objects
starColors.forEach((col, ci) => {
  const sc = document.createElement('canvas');
  sc.width = sc.height = 64;
  const sCtx = sc.getContext('2d');
  const g = sCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0,   col);
  g.addColorStop(0.4, col.replace(',1)', ',0.6)'));
  g.addColorStop(1,   col.replace(',1)', ',0)'));
  sCtx.fillStyle = g;
  sCtx.beginPath();
  sCtx.arc(32, 32, 32, 0, Math.PI * 2);
  sCtx.fill();

  // Each color gets its own subset of stars
  const count = 40;
  const sPos  = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    sPos[i * 3]     = (Math.random() - 0.5) * 30;
    sPos[i * 3 + 1] = Math.random() * 4 + 1;
    sPos[i * 3 + 2] = (Math.random() - 0.5) * 20;
  }
  const sGeo = new THREE.BufferGeometry();
  sGeo.setAttribute('position', new THREE.BufferAttribute(sPos, 3));

  const sMat = new THREE.PointsMaterial({
    size: 0.12 + Math.random() * 0.08,
    transparent: true,
    opacity: 0.7 + Math.random() * 0.3,
    map: new THREE.CanvasTexture(sc),
    alphaTest: 0.01,
    sizeAttenuation: true,
    depthWrite: false,
  });

  scene.add(new THREE.Points(sGeo, sMat));

  // Store mats for twinkle animation
  if (!this._starMats) this._starMats = [];
  this._starMats.push({ mat: sMat, base: sMat.opacity, phase: ci * 1.2 });
});

      // Animate
      const pos = geometry.attributes.position;
      let t = 0;
      const animate = () => {
        this._raf = requestAnimationFrame(animate);
        t += 0.008;
        starMat.opacity = (isDark ? 0.5 : 0.2) + Math.sin(t * 2) * (isDark ? 0.3 : 0.15);
        // Twinkle each color independently
        if (this._starMats) {
          this._starMats.forEach(s => {
            s.mat.opacity = s.base * (0.6 + 0.4 * Math.sin(t * 1.8 + s.phase));
          });
        }
        for (let iy = 0; iy <= SEGS; iy++) {
          for (let ix = 0; ix <= SEGS; ix++) {
            const idx = iy * N + ix;
            const x   = pos.getX(idx);
            const z   = pos.getZ(idx);
            const r   = Math.sqrt(x * x + z * z);
            pos.setY(idx,
              0.3  * Math.sin(x * 0.6 + t) * Math.cos(z * 0.4 + t * 0.7) +
              0.15 * Math.sin(r * 0.8 - t * 1.2) +
              0.1  * Math.cos(x * 1.1 - z * 0.5 + t * 0.5)
            );
          }
        }
        pos.needsUpdate = true;
        geometry.computeVertexNormals();
        wireGeo.copy(new THREE.WireframeGeometry(geometry));
        wireGeo2.copy(new THREE.WireframeGeometry(geometry));
        camera.position.x = Math.sin(t * 0.12) * 1.5;
        camera.position.y = 2.5 + Math.sin(t * 0.08) * 0.4;
        camera.lookAt(0, 0, 0);
        renderer.render(scene, camera);
      };
      animate();

      this._onResize = () => {
        const W2 = container.offsetWidth, H2 = container.offsetHeight;
        camera.aspect = W2 / H2;
        camera.updateProjectionMatrix();
        renderer.setSize(W2, H2);
      };
      window.addEventListener('resize', this._onResize);
    },

    // ── Foreground: fish inside MARVEL text ──────────────────
    initTitleCanvas() {
      const canvas = this.$refs.titleCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      const resize = () => {
        canvas.width  = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
      };
      this._titleResize = resize;
      window.addEventListener('resize', resize);
      resize();

      const rand = (a, b) => Math.random() * (b - a) + a;

      const COLORS = [
        ['#5b7fff','#3a5ce0'],
        ['#c77dff','#9b3de8'],
        ['#00e5ff','#00a3b8'],
        ['#ff7eb3','#e04080'],
        ['#7fffb2','#30c060'],
      ];

      const makeFish = () => {
        const [col1, col2] = COLORS[Math.floor(rand(0, COLORS.length))];
        const dir = Math.random() > 0.5 ? 1 : -1;
        const W = canvas.width, H = canvas.height;
        return {
          x:           dir > 0 ? rand(-100, 0) : rand(W, W + 100),
          y:           rand(H * 0.1, H * 0.9),
          vx:          dir * rand(0.5, 1.8),
          size:        rand(10, 28),
          wobble:      rand(0, Math.PI * 2),
          wobbleSpeed: rand(0.05, 0.10),
          wobbleAmp:   rand(3, 8),
          tailPhase:   rand(0, Math.PI * 2),
          tailSpeed:   rand(0.15, 0.25),
          col1, col2, dir,
        };
      };

      const fishes = Array.from({ length: 12 }, makeFish);

      const drawFish = (f) => {
        // const W = canvas.width, H = canvas.height;
        ctx.save();
        ctx.translate(f.x, f.y + Math.sin(f.wobble) * f.wobbleAmp);
        if (f.dir < 0) ctx.scale(-1, 1);
        const s = f.size;

        // Body
        const grad = ctx.createLinearGradient(-s, 0, s, 0);
        grad.addColorStop(0, f.col2);
        grad.addColorStop(0.5, f.col1);
        grad.addColorStop(1, f.col2);
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 0.9, s * 0.38, 0, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Tail
        const wag = Math.sin(f.tailPhase) * 0.5;
        ctx.beginPath();
        ctx.moveTo(-s * 0.8, 0);
        ctx.bezierCurveTo(-s*1.2, -s*0.4+wag*s*0.4, -s*1.6, -s*0.5+wag*s*0.5, -s*1.8, -s*0.55+wag*s*0.6);
        ctx.bezierCurveTo(-s*1.6,  s*0.0+wag*s*0.2, -s*1.2,  s*0.4+wag*s*0.3, -s*0.8, 0);
        ctx.fillStyle = f.col2;
        ctx.fill();

        // Dorsal fin
        ctx.beginPath();
        ctx.moveTo(s*0.1, -s*0.35);
        ctx.quadraticCurveTo(s*0.3, -s*0.85, s*0.5, -s*0.35);
        ctx.closePath();
        ctx.fillStyle = f.col1 + 'bb';
        ctx.fill();

        // Eye
        ctx.beginPath();
        ctx.arc(s*0.55, -s*0.08, s*0.1, 0, Math.PI*2);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(s*0.57, -s*0.08, s*0.055, 0, Math.PI*2);
        ctx.fillStyle = '#111';
        ctx.fill();

        ctx.restore();
      };

      const bubbles = Array.from({ length: 20 }, () => ({
        x:      rand(0, 1),
        y:      rand(0, canvas.height),
        r:      rand(1.5, 5),
        vy:     rand(0.3, 0.9),
        wobble: rand(0, Math.PI * 2),
      }));

      let fontSize = 100;
      let t = 0;

      const animate = () => {
        this._titleRaf = requestAnimationFrame(animate);
        t += 0.016;

        const W = canvas.width;
        const H = canvas.height;
        ctx.clearRect(0, 0, W, H);

        const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
        fontSize = Math.min(H * 0.88, W * 0.22);

        ctx.save();

        // 1. Draw text as base (defines pixels to paint on top of)
        ctx.font = `900 ${fontSize}px 'Bebas Neue', 'Arial Black', sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = isDark ? 'rgba(10,22,60,0.9)' : 'rgba(160,190,240,0.85)';
        ctx.fillText('MARVEL', W / 2, H / 2);

        // 2. Everything drawn after this is clipped to text pixels
        ctx.globalCompositeOperation = 'source-atop';

        // Water fill
        const wg = ctx.createLinearGradient(0, 0, 0, H);
        if (isDark) {
          wg.addColorStop(0, 'rgba(8,20,60,0.95)');
          wg.addColorStop(0.5, 'rgba(12,30,80,0.95)');
          wg.addColorStop(1, 'rgba(5,15,45,0.95)');
        } else {
          wg.addColorStop(0, 'rgba(120,160,230,0.95)');
          wg.addColorStop(0.5, 'rgba(90,140,220,0.95)');
          wg.addColorStop(1, 'rgba(60,110,200,0.95)');
        }
        ctx.fillStyle = wg;
        ctx.fillRect(0, 0, W, H);

        // Water ripples
        for (let i = 0; i < 6; i++) {
          const wy = H * 0.3 + Math.sin(t * 0.8 + i * 1.1) * H * 0.08;
          const rg = ctx.createLinearGradient(0, wy - 8, 0, wy + 8);
          rg.addColorStop(0, 'transparent');
          rg.addColorStop(0.5, isDark ? 'rgba(91,127,255,0.12)' : 'rgba(255,255,255,0.22)');
          rg.addColorStop(1, 'transparent');
          ctx.fillStyle = rg;
          ctx.fillRect(0, wy - 8, W, 16);
        }

        // Bubbles
        for (const b of bubbles) {
          b.y -= b.vy;
          b.wobble += 0.04;
          if (b.y < -10) { b.y = H + 10; b.x = rand(0, 1); }
          ctx.beginPath();
          ctx.arc(b.x * W + Math.sin(b.wobble) * 3, b.y, b.r, 0, Math.PI * 2);
          ctx.fillStyle = isDark ? 'rgba(150,200,255,0.25)' : 'rgba(255,255,255,0.5)';
          ctx.fill();
          ctx.strokeStyle = isDark ? 'rgba(150,200,255,0.4)' : 'rgba(255,255,255,0.75)';
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }

        // Fish
        for (const f of fishes) {
          f.x += f.vx;
          f.wobble += f.wobbleSpeed;
          f.tailPhase += f.tailSpeed;
          if (f.x > W + 120) { f.x = -80; f.y = rand(H * 0.1, H * 0.9); }
          if (f.x < -120)    { f.x = W + 80; f.y = rand(H * 0.1, H * 0.9); }
          drawFish(f);
        }

        ctx.restore();

        // 3. Glowing stroke outline on top — normal composite
        ctx.font = `900 ${fontSize}px 'Bebas Neue', 'Arial Black', sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.strokeStyle = isDark ? 'rgba(91,127,255,0.75)' : 'rgba(61,90,241,0.65)';
        ctx.lineWidth = 1.5;
        ctx.strokeText('MARVEL', W / 2, H / 2);
      };
      animate();
    },
  },
};
</script>


<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 2rem 80px;
  overflow: hidden;
}

.hero-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.hero-gradient-top {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 160px;
  background: linear-gradient(to bottom, var(--bg), transparent);
  z-index: 1;
}
.hero-gradient-bottom {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 200px;
  background: linear-gradient(to top, var(--bg), transparent);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
  width: 100%;
}

/* Eyebrow */
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  color: var(--accent3);
  border: 1px solid rgba(0,229,255,0.2);
  border-radius: 100px;
  padding: 6px 18px;
  margin-bottom: 2rem;
  background: rgba(0,229,255,0.05);
}
.eyebrow-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--accent3);
  animation: pulse 2s ease-in-out infinite;
}
.eyebrow-sep { color: var(--text3); }

@keyframes pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(0,229,255,0.4); }
  50% { opacity: 0.7; box-shadow: 0 0 0 8px rgba(0,229,255,0); }
}

/* Title */
.hero-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 0.9;
  margin-bottom: 1.5rem;
  gap: 0;
}
.title-line {
  font-family: 'Bebas Neue', sans-serif;
  letter-spacing: 0.06em;
  display: block;
}
.title-line-1 {
  font-size: clamp(5rem, 14vw, 12rem);
  background: linear-gradient(135deg, #fff 0%, var(--accent) 40%, var(--accent2) 70%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: titleShimmer 6s ease-in-out infinite;
}
.title-line-2 {
  font-size: clamp(3.5rem, 10vw, 8rem);
  color: var(--text2);
}
.title-plus {
  color: var(--accent);
  font-size: 0.65em;
  vertical-align: super;
}

@keyframes titleShimmer {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

/* Subtitle */
.hero-subtitle {
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  line-height: 1.7;
  color: var(--text2);
  margin-bottom: 2.5rem;
  font-weight: 400;
}

/* Stats */
.hero-stats {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2.5rem;
}
.stat-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 24px;
  background: var(--surface);
  border: 1px solid var(--border2);
  border-radius: 14px;
  backdrop-filter: blur(12px);
  min-width: 90px;
  transition: border-color var(--transition), transform var(--transition);
}
.stat-chip:hover {
  border-color: var(--accent);
  transform: translateY(-3px);
}
.stat-val {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.8rem;
  letter-spacing: 0.04em;
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.stat-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  color: var(--text3);
  text-transform: uppercase;
  margin-top: 2px;
}

/* CTAs */
.hero-ctas {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 3rem;
}
.cta-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 28px;
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  color: #fff;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.03em;
  transition: all var(--transition);
  box-shadow: 0 4px 24px var(--glow);
}
.cta-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 36px var(--glow);
  filter: brightness(1.1);
}
.cta-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 24px;
  border: 1px solid var(--border2);
  color: var(--text);
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  background: var(--surface);
  backdrop-filter: blur(12px);
  transition: all var(--transition);
}
.cta-secondary:hover {
  border-color: var(--accent);
  background: rgba(91,127,255,0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 20px var(--glow);
  color: var(--text);
}

/* Scroll hint */
.scroll-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  color: var(--text3);
  text-transform: uppercase;
}
.scroll-line {
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, transparent, var(--accent));
  animation: scrollDrop 2s ease-in-out infinite;
}
@keyframes scrollDrop {
  0%, 100% { transform: scaleY(0); transform-origin: top; opacity: 0; }
  50% { transform: scaleY(1); transform-origin: top; opacity: 1; }
}

/* Teaser image */
.hero-teaser {
  position: relative;
  z-index: 2;
  margin-top: 4rem;
  width: 100%;
  max-width: 900px;
}
.teaser-frame {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--border2);
  box-shadow: 0 0 60px rgba(91,127,255,0.15), 0 40px 80px rgba(0,0,0,0.5);
}
.teaser-frame img {
  width: 100%;
  display: block;
  filter: brightness(0.85) saturate(1.1);
}
.teaser-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(3,5,15,0.8) 0%, transparent 50%);
}
.teaser-label {
  position: absolute;
  bottom: 16px; left: 24px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text2);
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 5px 12px;
  border-radius: 6px;
  backdrop-filter: blur(8px);
}

/* Reveal animation */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 600px) {
  .hero { padding: 100px 1rem 60px; }
  .hero-stats { gap: 0.5rem; }
  .stat-chip { padding: 10px 16px; min-width: 70px; }
  .stat-val { font-size: 1.4rem; }
  .hero-ctas { flex-direction: column; align-items: center; }
  .cta-primary, .cta-secondary { width: 100%; justify-content: center; }
}
</style>